import { useNavigate } from "@solidjs/router";
import {
  AiOutlineArrowRight,
  AiOutlineCalendar,
  AiOutlineClockCircle,
  AiOutlineUser,
} from "solid-icons/ai";
import { type Component, createMemo, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import {
  JobCard,
  JobCardContent,
  JobCardHeader,
} from "@/components/shared/jobs/job-card-primitives";
import { JobOfferBadges } from "@/components/shared/jobs/job-offer-badges";
import { Badge } from "@/components/ui/badge";
import { CardTitle } from "@/components/ui/card";
import { cn } from "@/libs/cn";
import { formatDateAgo, formatReadableDate } from "@/libs/date";
import { PROPOSAL_STATE_VARIANTS } from "../constants/proposal-state-variants";
import type { ReceivedProposalItem } from "../types/proposals";
import { proposalToJobForBadges } from "../utils/proposal-mappers";

export const ProposalCard: Component<{ proposal: ReceivedProposalItem }> = (
  props,
) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const proposal = props.proposal;
  const jobForBadges = proposalToJobForBadges(proposal);
  const isInactive = createMemo(() => proposal.jobOffer.is_active === false);

  const truncatedCoverLetter = () => {
    const letter = proposal.cover_letter;
    if (!letter) return null;
    return letter.length > 150 ? `${letter.slice(0, 150)}...` : letter;
  };

  return (
    <JobCard
      class={cn(
        "border border-gray-200 shadow-none max-w-full bg-white rounded-lg w-full border-collapse relative cursor-pointer lg:mx-auto transition-shadow duration-200 hover:shadow-md",
        isInactive() &&
          "bg-muted text-muted-foreground border border-dashed border-muted opacity-70 hover:bg-muted hover:shadow-none",
      )}
      onClick={() => navigate(`/profile/proposals/${proposal.id}`)}
    >
      <JobCardHeader
        class="pb-3"
        action={
          <AiOutlineArrowRight
            size={20}
            class={cn("text-black", isInactive() && "text-muted-foreground")}
          />
        }
      >
        <div class="flex items-start gap-3 flex-1 min-w-0">
          <div class="w-12 h-12 bg-gray-200 rounded flex items-center justify-center overflow-hidden shrink-0">
            <Show
              when={proposal.jobOffer.company.logo_url}
              fallback={
                <div class="w-full h-full bg-gray-300 flex items-center justify-center text-gray-600 font-semibold text-lg">
                  {proposal.jobOffer.company.name?.[0]?.toUpperCase() ?? "A"}
                </div>
              }
            >
              <img
                src={proposal.jobOffer.company.logo_url!}
                alt={proposal.jobOffer.company.name}
                class="w-full h-full object-cover"
              />
            </Show>
          </div>
          <div class="flex flex-col items-center lg:items-start gap-1 flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <CardTitle
                class={cn(
                  "text-lg font-bold text-black",
                  isInactive() && "text-muted-foreground",
                )}
              >
                {proposal.jobOffer.title}
              </CardTitle>
              <Show when={!proposal.jobOffer.is_active}>
                <Badge variant="secondary" size="sm" class="ml-2">
                  {t("joblist.jobOffer.badge.closed")}
                </Badge>
              </Show>
              <Badge
                variant={PROPOSAL_STATE_VARIANTS[proposal.proposal_state]}
                size="sm"
                class="ml-2"
              >
                {t(`profile.tabs.proposals.status.${proposal.proposal_state}`)}
              </Badge>
              <Show
                when={
                  proposal.proposal_state === "accepted" &&
                  proposal.meeting_date
                }
                fallback={
                  <Show
                    when={
                      proposal.proposal_state === "accepted" &&
                      !proposal.meeting_date
                    }
                  >
                    <Badge
                      variant="outline"
                      size="sm"
                      class="ml-2 border-amber-300 text-amber-700 bg-amber-50"
                    >
                      <AiOutlineCalendar class="w-3 h-3 mr-1" />
                      {t("profile.tabs.proposals.card.no_meeting")}
                    </Badge>
                  </Show>
                }
              >
                <Badge variant="outline" size="sm" class="ml-2">
                  <AiOutlineCalendar class="w-3 h-3 mr-1" />
                  {t("profile.tabs.proposals.card.meeting_date", {
                    date: formatReadableDate(proposal.meeting_date!),
                  })}
                </Badge>
              </Show>
            </div>
            <div class="flex items-center gap-2 flex-wrap">
              <p
                class={cn(
                  "text-sm",
                  isInactive() ? "text-muted-foreground" : "text-gray-700",
                )}
              >
                {proposal.jobOffer.company.name}
              </p>
              <div class="flex items-center gap-3 text-xs text-gray-500">
                <div class="flex items-center gap-1">
                  <AiOutlineClockCircle
                    size={14}
                    class={cn(isInactive() && "text-muted-foreground")}
                  />
                  <span>
                    {t("joblist.jobOffer.badge.date", {
                      date: formatDateAgo(proposal.created_at) as string,
                    })}
                  </span>
                </div>
                <div class="flex items-center gap-1">
                  <AiOutlineUser
                    size={14}
                    class={cn(isInactive() && "text-muted-foreground")}
                  />
                  <span>{proposal.jobOffer.applications_count ?? 0}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </JobCardHeader>
      <JobCardContent>
        <JobOfferBadges job={jobForBadges} isInactive={isInactive} />
        <Show when={truncatedCoverLetter()}>
          <div class="flex items-center w-full pt-2">
            <p
              class={cn(
                "text-xs",
                isInactive() ? "text-muted-foreground" : "text-gray-500",
              )}
            >
              {truncatedCoverLetter()}
            </p>
          </div>
        </Show>
      </JobCardContent>
    </JobCard>
  );
};
